import React from 'react';
import { Navigate, Outlet, useLocation } from 'react-router-dom';
import { Loader2 } from 'lucide-react';
import { useAuth } from '@context/AuthContext';
import SeoHead from '@components/SeoHead';

interface RoleRouteProps {
  role: 'roommate_seeker' | 'landlord';
}

const RoleRoute: React.FC<RoleRouteProps> = ({ role }) => {
  const { currentUser, userRole, profileComplete, loading } = useAuth();
  const location = useLocation();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50" role="status" aria-live="polite">
        <Loader2 className="w-10 h-10 text-pink-500 animate-spin" aria-hidden="true" />
        <span className="sr-only">Loading...</span>
      </div>
    );
  }

  if (!currentUser) {
    return <Navigate to="/login" replace />;
  }

  if (!userRole) {
    return <Navigate to="/signup" replace />;
  }

  if (userRole !== role) {
    return (
      <Navigate
        to={userRole === 'landlord' ? '/landlord/dashboard' : '/tenant/swipe'}
        replace
      />
    );
  }

  // Force new users through onboarding before they can reach the rest of the app.
  const onboardingPath = role === 'landlord' ? '/landlord/onboarding' : '/tenant/onboarding';
  if (!profileComplete && location.pathname !== onboardingPath) {
    return <Navigate to={onboardingPath} replace />;
  }

  return (
    <>
      <SeoHead title="App" path={location.pathname} noindex />
      <Outlet />
    </>
  );
};

export default RoleRoute;